import React from 'react';
import { Filter, X } from 'lucide-react';
import { useSlotSearch } from './SlotSearchContext';

const SLOT_TYPES = ['common', 'preferred', 'premium'];

export default function SlotFilterPanel() {
  const { filters, updateFilters, clearFilters } = useSlotSearch();

  const activeFilterCount = [
    filters.slot_type && filters.slot_type.length > 0,
    filters.has_power !== undefined,
    filters.min_price !== undefined || filters.max_price !== undefined,
    filters.is_available !== undefined && filters.is_available !== true,
    filters.search && filters.search.length > 0,
  ].filter(Boolean).length;

  const handleSlotTypeChange = (type: string) => {
    const currentTypes = filters.slot_type || [];
    if (currentTypes.includes(type)) {
      updateFilters({ slot_type: currentTypes.filter(t => t !== type) });
    } else {
      updateFilters({ slot_type: [...currentTypes, type] });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-primary rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      {/* Search by slot number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search Slot Number
        </label>
        <input
          type="text"
          placeholder="e.g., A1, B12..."
          value={filters.search || ''}
          onChange={(e) => updateFilters({ search: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Slot Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Slot Type
        </label>
        <div className="space-y-2">
          {SLOT_TYPES.map((type) => (
            <label key={type} className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={(filters.slot_type || []).includes(type)}
                onChange={() => handleSlotTypeChange(type)}
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Power Availability */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Power Availability
        </label>
        <div className="space-y-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="power"
              checked={filters.has_power === undefined}
              onChange={() => updateFilters({ has_power: undefined })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Any</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="power"
              checked={filters.has_power === true}
              onChange={() => updateFilters({ has_power: true })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Power Required</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="power"
              checked={filters.has_power === false}
              onChange={() => updateFilters({ has_power: false })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">No Power Needed</span>
          </label>
        </div>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Price Range
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="number"
              placeholder="Min"
              min="0"
              value={filters.min_price ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  updateFilters({ min_price: undefined });
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    updateFilters({ min_price: numValue });
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <span className="text-gray-500 self-center">-</span>
          <div className="flex-1">
            <input
              type="number"
              placeholder="Max"
              min="0"
              value={filters.max_price ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  updateFilters({ max_price: undefined });
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    updateFilters({ max_price: numValue });
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Availability
        </label>
        <div className="space-y-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="availability"
              checked={filters.is_available === true}
              onChange={() => updateFilters({ is_available: true })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Available Only</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="availability"
              checked={filters.is_available === undefined}
              onChange={() => updateFilters({ is_available: undefined })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Show All</span>
          </label>
        </div>
      </div>
    </div>
  );
}
